/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { isNumericTableColumnType } from '@utils/universalTracker';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { QUESTION } from '@constants/terminology';
import { QuestionData } from '@routes/custom-dashboard/utils';
import { IntegrationSource } from '@g17eco/types/app-integration';

interface ProviderSelection {
  questionData: QuestionData;
  questions: IntegrationSource['questions'];
  onChange: (update: Pick<QuestionData, 'code' | 'valueListCode'>) => void;
}

export const ProviderQuestionSelection = ({ questionData, questions, onChange }: ProviderSelection) => {


  const options = useMemo(() => {
    return questions.map((question) => {
        return {
          label: question.name,
          searchString: question.name,
          value: question.code,
          columns: question.valueValidation?.table?.columns?.reduce((acc, col) => {
            acc.push({
              label: col.name,
              searchString: col.name,
              value: col.code,
              isDisabled: !isNumericTableColumnType(col.type)
            });
            return acc;
          }, [] as Option[]) ?? [],
        };
      }
    );
  }, [questions])


  if (options.length === 0) {
    return (
      <div>
        <p className='mt-3 text-medium'>No questions available</p>
      </div>
    )
  }

  const questionOption = options.find((v) => v.value === questionData.code) ?? null;
  const questionInputs = questionOption?.columns ?? [];
  const selectedInput = questionInputs.find((v) => v.value === questionData.valueListCode) ?? null;

  return (
    <>
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        className='mt-3'
        placeholder={`Select ${QUESTION.SINGULAR}`}
        options={options}
        value={questionOption}
        onChange={(option) => onChange({ code: option?.value, valueListCode: '' })}
      />
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        isDisabled={questionInputs.every((v) => v.isDisabled)}
        className='mt-3'
        placeholder='Select input'
        options={questionInputs}
        value={selectedInput}
        onChange={(option) => onChange({ code: questionOption?.value, valueListCode: option?.value ?? undefined })}
      />
    </>
  )

}
